import { spreadAndtransformDefaultValues } from '@common/components/forms';
import { PageCard } from '@common/components/layout'
import { groupSalesItems } from '@common/helpers';
import { IProactiveRxRuleForm, ProactivePrescriptionVeterinarianRuleModel } from '@common/models';
import { NotificationStoreInstance, useProactivePrescriptionRuleStore } from '@common/stores';
import { ProactivePrescriptionDetailsHeader, ProactivePrescriptionDisabledBanner, ProactivePrescriptionRuleDetailsTable } from '@common/views/proactivePrescriptions';
import { useCreateVeterinarianStore } from '@hospital/stores';
import { observer } from 'mobx-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Card } from 'react-bootstrap';
import { Form, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { YesNoConfirmModal } from '@common/components/modals';
import { UserProactivePrescriptionDetailsWrapper } from './UserProactivePrescriptionDetailsWrapper';
import { useRouteBreadcrumb } from '@common/hooks';

export const UserProactivePrescriptionDetails = observer(() => {
  const navigate = useNavigate();
  const { proactivePrescriptionRuleId, userId } = useParams();
  const parsedProactiveRxRuleId = useMemo(() => parseInt(proactivePrescriptionRuleId!), [proactivePrescriptionRuleId])
  const parsedUserId = useMemo(() => parseInt(userId!), [userId])
  const proactiveRxStore = useProactivePrescriptionRuleStore();
  const vetStore = useCreateVeterinarianStore();
  const [localVetRule, setLocalVetRule] = useState<ProactivePrescriptionVeterinarianRuleModel | undefined>(undefined);
  const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);

  useEffect(() => {
    vetStore.fetchVeterinarianByUserId(parsedUserId);
  }, [parsedUserId, vetStore])


  useEffect(() => {
    if (vetStore.veterinarian) {
      const matchedRule = vetStore.veterinarian.proactiveRxVetRules.find(
        vetRule => vetRule.proactivePrescriptionRuleId === parsedProactiveRxRuleId
      );
      setLocalVetRule(
        matchedRule ??
        new ProactivePrescriptionVeterinarianRuleModel({
          proactivePrescriptionRuleId: parsedProactiveRxRuleId,
          veterinarianId: vetStore.veterinarian.veterinarianId,
          proactivePrescriptionVeterinarianRuleItems: [],
          rowVersion: '',
        })
      );
    }
  }, [parsedProactiveRxRuleId, vetStore.veterinarian]);

  useEffect(() => {
    proactiveRxStore.getProactivePrescriptionRule(parsedProactiveRxRuleId);
  }, [parsedProactiveRxRuleId, proactiveRxStore]);


  const hospitalResult = useMemo(() => {
    const proactiveRxRule = proactiveRxStore.selectedProactivePrescriptionRule;
    const sourceRuleItems = proactiveRxRule?.proactivePrescriptionRuleItems.map(rule => rule.salesItemOptionId);
    const vetRuleItems = localVetRule?.proactivePrescriptionVeterinarianRuleItems.map(rule => rule.salesItemOptionId);
    const result = groupSalesItems(proactiveRxRule?.hospitalTraitId, proactiveRxRule?.speciesId, sourceRuleItems, proactiveRxStore.salesItemOptionsByProactivePrescriptionRule, true, !vetStore.veterinarian?.overrideHospitalProactivePrescriptions ? sourceRuleItems : vetRuleItems)
    return result;
  }, [localVetRule?.proactivePrescriptionVeterinarianRuleItems, proactiveRxStore.salesItemOptionsByProactivePrescriptionRule, proactiveRxStore.selectedProactivePrescriptionRule, vetStore.veterinarian?.overrideHospitalProactivePrescriptions]);

  const dataSource = useMemo<IProactiveRxRuleForm>(() => ({
    hospitalList: hospitalResult,
    clinicList: []
  }), [hospitalResult]);

  const formMethods = useForm<IProactiveRxRuleForm>({
    defaultValues: spreadAndtransformDefaultValues(dataSource),
  });

  const { handleSubmit, reset, getValues } = formMethods;

  const originalValuesRef = useRef<IProactiveRxRuleForm>(dataSource);
  const { setBreadCrumb } = useRouteBreadcrumb();

  useEffect(() => {
    if (proactiveRxStore.selectedProactivePrescriptionRule) {
      reset(dataSource);
      originalValuesRef.current = dataSource;
      setBreadCrumb(proactiveRxStore.selectedProactivePrescriptionRule.name);
    }
  }, [proactiveRxStore.selectedProactivePrescriptionRule, reset, dataSource, setBreadCrumb]);

  const onSubmit = (data: IProactiveRxRuleForm) => {
    const updatedValue = proactiveRxStore.updateProactiveRxVetRuleObject(data, localVetRule ?? new ProactivePrescriptionVeterinarianRuleModel());
    if (updatedValue.proactivePrescriptionVeterinarianRuleId === 0) {
      proactiveRxStore.addProactiveRxVetRule(updatedValue)
        .then(() => NotificationStoreInstance.success('Proactive Prescription Veterinarian Rule updated successfully'))
        .catch((err) => NotificationStoreInstance.error(`${err}`));
    }
    else {
      proactiveRxStore.updateProactiveRxVetRule(updatedValue)
        .then(() => {
          // 🧩 WHY WE CALL reset(getValues()) AFTER SAVE
          //
          // React Hook Form caches each field's validation rules (which is a function) when they're first registered.
          // Our FormSelect component wraps a <Controller>, which doesn't re-register validation
          // when dependent values (like toggleValue in ProactivePrescriptionRuleSalesItemRow.tsx) change.
          //
          // After saving, we stay on the same screen and RHF still holds stale validation
          // closures from the previous state — causing "Required" to show incorrectly on
          // subsequent edits.
          //
          // Calling `reset(getValues())` forces RHF to:
          //   ✅ Unregister all existing fields and validation rules
          //   ✅ Re-register them with the *current* toggle states and values
          //   ✅ Preserve all field values and UI state (nothing gets cleared)
          reset(getValues());
          NotificationStoreInstance.success('Proactive Prescription Veterinarian Rule updated successfully')
        })
        .catch((err) => {
          reset(getValues());
          NotificationStoreInstance.error(`${err}`)
        });
    }
    originalValuesRef.current = data;
  }

  const handleCancel = () => {
    const currentValues = formMethods.getValues();
    if (!_.isEqual(currentValues, originalValuesRef.current)) {
      setShowCancelConfirm(true);
    }
    else {
      navigate('../../')
    }
  }

  return (
    <>
      <YesNoConfirmModal
        visible={showCancelConfirm}
        title="Cancel Changes"
        description="You have unsaved changes, are you sure you want to cancel?"
        busy={false}
        cancelButtonText="No"
        onConfirm={() => {
          setShowCancelConfirm(false);
          reset(dataSource);
          navigate('../../');
        }}
        onCancel={() => setShowCancelConfirm(false)}
      />
      <UserProactivePrescriptionDetailsWrapper>
        <h1 className='page-header'>{proactiveRxStore.selectedProactivePrescriptionRule?.name}</h1>
        <PageCard title='Proactive Prescription Details' topBanner={vetStore.veterinarian?.optOutOfProactivePrescription ? <ProactivePrescriptionDisabledBanner /> : <></>}>
          <ProactivePrescriptionDetailsHeader onSubmit={handleSubmit(onSubmit)} onCancel={handleCancel} disableSave={!vetStore.veterinarian?.overrideHospitalProactivePrescriptions} />
        </PageCard>
        <FormProvider {...formMethods}>
          <Form>
            <PageCard className='mt-3' title='Hospital'>
              {vetStore.veterinarian?.overrideHospitalProactivePrescriptions &&
                <Card className='p-0 mb-2' bg='light-blue'>
                  <Card.Body className='p-1 ms-1'>
                    <p className='mb-0'>
                      <strong>Note:</strong> You have opted out of using suggested medications. Proactive prescribing remains active, but you must set your own medication preferences
                      <strong>
                        {' '}
                        per rule and species
                      </strong>
                      {' '}
                      for it work. If no preference is set,
                      <strong>
                        {' '}
                        PetPass will not proactively prescribe
                      </strong>{' '}
                      for that scenario. To set your preferences, opt your suggested medication in per weight and set the option you would like to proactively prescribe.
                    </p>
                  </Card.Body>
                </Card>
              }
              <ProactivePrescriptionRuleDetailsTable
                dataSource={dataSource.hospitalList}
                listName="hospitalList"
                isHospital
                isDisabled={!vetStore.veterinarian?.overrideHospitalProactivePrescriptions!}
              />
            </PageCard>
          </Form>
        </FormProvider>
      </UserProactivePrescriptionDetailsWrapper>
    </>
  )
});
